import { Trans } from '@lingui/macro';
import { Experience, PointOfInterest, Visit } from '@milo/types/index';
import { getBounds } from 'geolib';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { debounce, groupBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import useSupercluster from 'use-supercluster';
import {
  getExperiences,
  isNativeApp,
  useAuthentication,
  useFeatureFlag,
  useGeoLocation,
  useLocalStorage,
  usePointsOfInterest,
  useQuery,
  useVerticals,
} from '../../hooks';
import {
  CreativiteLocale,
  CurrentPosition,
  DocumentIcon,
  Filter,
  Heart,
  PlaisirsGourmands,
  Sejours,
  Sevader,
  ZoomIn,
  ZoomOut,
} from '../../icons';
import { MiloRoundLogo } from '../../logos';
import { ReactFC } from '../../types';
import { colorFn, ColorType, formatCurrency, palette, zIndexFn } from '../../utils';
import { AddWishlistModal } from '../AddWishlistModal';
import { ImageKitImage } from '../Image';
import { Link2 } from '../Link';
import { MapFilterModal } from '../Modal/Filter';
import { Text } from '../Text';
import { MiloMapLoader, MiloMapProps } from './';

import {
  EXPERIENCE_MARKER_CLASS_PREFIX,
  MAP_DEFAULT_CENTER,
  MAP_DEFAULT_ZOOM,
  MAP_ID,
  MAP_LAST_CLICKED_EXPERIENCE_LS_KEY,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  MAP_SAVED_BOUNDS_LS_KEY,
  MAP_SAVED_CENTER_LS_KEY,
  MAP_SAVED_ZOOM_LS_KEY,
  MAP_TILE_LAYER_URL,
} from '@milo/constants';
import { convertDateToReadableFormat } from '@milo/helpers';

type MapMarkerType = 'experience' | 'point_of_interest' | 'cluster';

const MapPlaceholder: ReactFC = () => {
  return (
    <p>
      <noscript>
        Besoin de javascript pour utiliser la carte.
        <br />
        Need javascript to use the map.
      </noscript>
    </p>
  );
};

const leafletCustomStyles = `
  .leaflet-control-attribution {
    display: none !important;
  }

  .leaflet-popup-close-button {
    font-weight: bold !important;
    font-size: 25px !important;
    right: 0px !important;
    top: 0px !important;
    color: black !important;
    width: 40px !important;
    height: 40px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .leaflet-popup-content {
    margin: 0 !important;
  }

  .leaflet-popup-content-wrapper {
    border: 0 solid ${colorFn('white')} !important;
  }

  .leaflet-marker-icon {
    background: none !important;
    border: none !important;
    outline: none !important;
  }

  .milo-cluster-marker {
    z-index: 50 !important;
  }

  .experience-marker {
    z-index: 100 !important;
  }

  .milo-current-position-marker {
    z-index: 10 !important;
  }
`;

const insertLeafletCustomStyles = () => {
  const leafletStyleNodeId = 'leaflet-styles';
  const leafletStyleNode = document.getElementById(leafletStyleNodeId);

  if (!leafletStyleNode) {
    const styleNode = document.createElement('style');
    styleNode.id = leafletStyleNodeId;
    styleNode.innerHTML = leafletCustomStyles;
    document.body.appendChild(styleNode);
  }
};

const MiloMapContainer: ReactFC<MiloMapProps> = ({
  activeFiltersCount,
  children,
  className,
  experiences = [],
  includeCurrentPositionInBounds = false,
  onClickFilter,
  zoomToFitPassedExperiences = false,
  ...props
}) => {
  const { user, isLoading: userLoading } = useAuthentication();
  const { lat: userLat, lng: userLng, permission } = useGeoLocation();
  const [savedMapCenter] = useLocalStorage(MAP_SAVED_CENTER_LS_KEY, MAP_DEFAULT_CENTER);
  const [savedMapZoom] = useLocalStorage(MAP_SAVED_ZOOM_LS_KEY, MAP_DEFAULT_ZOOM);

  const { isLoading: featureFlagsLoading, data: mapFiltersActive } = useFeatureFlag(
    'map-filters',
    { employee: (user?.email ?? '').includes('@miloguide.com') || (user?.email ?? '').includes('@mtaregion.com') },
    { enabled: !userLoading }
  );

  useEffect(() => {
    insertLeafletCustomStyles();
  }, []);

  const mapRef = useRef<L.Map>(null);
  const loading = featureFlagsLoading || userLoading;

  if (loading) {
    return <MiloMapLoader />;
  }

  const userLocationUnknown = !userLat || !userLng || permission !== 'granted';

  const flyToCurrentLocation = () => {
    if (userLocationUnknown) return null;
    mapRef.current.closePopup();
    mapRef?.current?.flyTo([userLat, userLng], 12, { animate: true, duration: 1 });
  };

  const zoomIn = (delta: number = 1) => {
    const currentZoom = mapRef?.current?.getZoom();
    const currentCenter = mapRef?.current?.getCenter();
    const desiredZoom = currentZoom + delta >= MAP_MAX_ZOOM ? MAP_MAX_ZOOM : currentZoom + delta;
    mapRef.current.flyTo(currentCenter, desiredZoom, { animate: true, duration: 0.25 });
  };

  const zoomOut = (delta: number = 1) => {
    mapRef?.current?.zoomOut(delta);
  };

  // If we want to automatically zoom on the passed experiences like in the guides
  // for example, we pass the calculated bounds only to the map component
  // Otherwise, we only pass the center and the zoom level we want.
  const calculatedProperties = (() => {
    if (zoomToFitPassedExperiences && experiences.length > 0) {
      const latLngs = (() => {
        let arr = experiences.map((exp) => [exp.location?.lng, exp.location?.lat]);

        if (includeCurrentPositionInBounds && !userLocationUnknown) {
          arr = [...arr, [userLng, userLat]];
        }

        return arr;
      })();

      const mapBoundsFromPassedExperiences = getBounds(latLngs);

      const southWest = L.latLng(mapBoundsFromPassedExperiences.minLat, mapBoundsFromPassedExperiences.minLng);
      const northEast = L.latLng(mapBoundsFromPassedExperiences.maxLat, mapBoundsFromPassedExperiences.maxLng);
      const bounds = L.latLngBounds(southWest, northEast);

      return { bounds };
    }

    return {
      center: savedMapCenter,
      zoom: savedMapZoom,
    };
  })();

  return (
    <div className="relative h-full w-full" data-testid="milo-map">
      <MapContainer
        ref={mapRef}
        id={MAP_ID}
        className={`h-full w-full ` + className}
        zoomControl={false}
        scrollWheelZoom={true}
        placeholder={<MapPlaceholder />}
        wheelPxPerZoomLevel={100}
        minZoom={MAP_MIN_ZOOM}
        maxZoom={MAP_MAX_ZOOM}
        {...calculatedProperties}
      >
        <TileLayer
          /* The TileLayer is the component that governs the styling of the map, we can swap it easily by finding some online */
          url={MAP_TILE_LAYER_URL}
        />
        <MiloCurrentPositionMarker />
        {children}
      </MapContainer>
      <div
        className={`absolute right-2 flex flex-col gap-2 ${isNativeApp() ? 'bottom-[12%]' : 'bottom-2'}`}
        style={{ zIndex: zIndexFn('navigation', -1) }}
      >
        <button
          onClick={() => flyToCurrentLocation()}
          className="group relative flex h-10 w-10 items-center justify-center rounded-full bg-white p-0 text-xl font-bold shadow-lg"
        >
          {userLocationUnknown && (
            <>
              <div className="absolute right-[110%] w-fit  whitespace-nowrap rounded-lg bg-dark p-2 text-left text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                <Trans>
                  Géolocalisation désactivée. <br />
                  Activez dans vos préférences pour utiliser.
                </Trans>
              </div>
              <div className="absolute  h-full w-full rounded-full border-4 border-red opacity-50">
                <div className="absolute left-1/2 top-0 h-full w-1 -translate-x-1/2 -rotate-45 bg-red "></div>
              </div>
            </>
          )}

          <CurrentPosition className="!w-1/2" />
        </button>
        {mapFiltersActive && (
          <button
            onClick={onClickFilter}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-0 text-xl font-bold shadow-lg"
          >
            {activeFiltersCount > 0 && (
              <div className="absolute -right-[5px] top-[40px] h-5 w-5 rounded-full bg-raspberry text-center text-white">
                <Text>{activeFiltersCount}</Text>
              </div>
            )}
            <Filter className="!w-1/2" />
          </button>
        )}

        {!isNativeApp() && (
          <>
            <button
              onClick={() => zoomIn()}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-0 text-xl font-bold shadow-lg"
            >
              <ZoomIn className="!w-1/2" />
            </button>
            <button
              onClick={() => zoomOut()}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-0 text-xl font-bold shadow-lg"
            >
              <ZoomOut className="!w-1/2" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CurrentPositionMarkerSvg = L.divIcon({
  html: `
    <svg width="30px" height="30px" viewBox="0 0 20 20">
      <animate xlink:href="#miloMapCurrentPositionMarkerBackLayer" attributeName="r" from="5.3" to="10" dur="1.5s" begin="0s" repeatCount="indefinite" id="circ-anim" />
      <animate xlink:href="#miloMapCurrentPositionMarkerBackLayer" attributeType="CSS" attributeName="opacity" from="1" to="0" dur="1.5s" begin="0s" repeatCount="indefinite" id="circ-anim" />
      <circle id="miloMapCurrentPositionMarkerBackLayer" st cx="10" cy="10" r="6.5" fill="transparent" stroke=${palette['focus-blue']} strokeWidth="1"/>
      <circle fill=${palette['focus-blue']} cx="10" cy="10" r="5.5"/>
    </svg>
  `,
  className: 'milo-current-position-marker',
});

const MiloCurrentPositionMarker: ReactFC = () => {
  const { lat, lng, permission } = useGeoLocation();

  if (permission === 'denied' || !lat || !lng) {
    return null;
  }

  return <Marker icon={CurrentPositionMarkerSvg} position={[lat, lng]} />;
};

const verticalsDesign: {
  [x: number]: { htmlIcon: (classes: string) => string; color: ColorType; reactIcon: ReactFC };
} = {
  1: {
    // Plaisirs gourmands
    reactIcon: PlaisirsGourmands,
    htmlIcon: (classes) =>
      `<svg class="${classes}" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 16.25H12" stroke="currentColor" strokeWidth="1.27939" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 11.75V16.25" stroke="currentColor" strokeWidth="1.27939" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 3.50002L12.75 8.00002C12.75 8.99459 12.3549 9.94841 11.6517 10.6517C10.9484 11.3549 9.99456 11.75 9 11.75C8.00544 11.75 7.05161 11.3549 6.34835 10.6517C5.64509 9.94841 5.25 8.99459 5.25 8.00002L6 3.50002H12Z" stroke="currentColor" strokeWidth="1.27939" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    color: 'warning',
  },
  2: {
    // Séjours
    reactIcon: Sejours,
    htmlIcon: (classes) =>
      `<svg viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 5.16663V12.5M2 9.8333H14M14 12.5V7.16663C14 6.81301 13.8595 6.47387 13.6095 6.22382C13.3594 5.97378 13.0203 5.8333 12.6667 5.8333H7.33333V9.8333" stroke="currentColor" strokeWidth="1.3282" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.66667 7.83332C5.03486 7.83332 5.33333 7.53484 5.33333 7.16665C5.33333 6.79846 5.03486 6.49998 4.66667 6.49998C4.29848 6.49998 4 6.79846 4 7.16665C4 7.53484 4.29848 7.83332 4.66667 7.83332Z" stroke="currentColor" strokeWidth="1.3282" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    color: 'teal',
  },
  3: {
    // S'évader
    reactIcon: Sevader,
    htmlIcon: (classes) =>
      `<svg class="${classes}" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.66667 3.83333C9.03486 3.83333 9.33333 3.53486 9.33333 3.16667C9.33333 2.79848 9.03486 2.5 8.66667 2.5C8.29848 2.5 8 2.79848 8 3.16667C8 3.53486 8.29848 3.83333 8.66667 3.83333Z" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.66797 11.8333L6.0013 12.5L6.5013 11.5" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.9987 14.5V11.8333L7.33203 9.83334L7.9987 5.83334" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.66797 8.5V6.5L8.0013 5.83334L10.0013 7.83334L12.0013 8.5" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    color: 'green',
  },
  4: {
    // Créativité Locale
    reactIcon: CreativiteLocale,
    htmlIcon: (classes) =>
      `<svg class="${classes}" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 14.5C7.21207 14.5 6.43185 14.3448 5.7039 14.0433C4.97595 13.7417 4.31451 13.2998 3.75736 12.7426C3.20021 12.1855 2.75825 11.5241 2.45672 10.7961C2.15519 10.0681 2 9.28793 2 8.5C2 7.71207 2.15519 6.93185 2.45672 6.2039C2.75825 5.47595 3.20021 4.81451 3.75736 4.25736C4.31451 3.70021 4.97595 3.25825 5.7039 2.95672C6.43185 2.65519 7.21207 2.5 8 2.5C9.5913 2.5 11.1174 3.0619 12.2426 4.0621C13.3679 5.06229 14 6.41885 14 7.83333C14 8.54058 13.6839 9.21885 13.1213 9.71895C12.5587 10.219 11.7956 10.5 11 10.5H9.33333C9.03591 10.4952 8.74542 10.59 8.50808 10.7693C8.27074 10.9487 8.10018 11.2022 8.02354 11.4896C7.94689 11.777 7.96856 12.0818 8.0851 12.3555C8.20163 12.6292 8.40635 12.8561 8.66667 13C8.79978 13.1228 8.89106 13.2843 8.92769 13.4616C8.96433 13.639 8.94447 13.8234 8.87091 13.9889C8.79735 14.1544 8.67378 14.2927 8.51758 14.3844C8.36137 14.4761 8.18037 14.5165 8 14.5" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.0013 7.83334C5.1854 7.83334 5.33464 7.6841 5.33464 7.50001C5.33464 7.31591 5.1854 7.16667 5.0013 7.16667C4.81721 7.16667 4.66797 7.31591 4.66797 7.50001C4.66797 7.6841 4.81721 7.83334 5.0013 7.83334Z" fill="black" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.0013 5.83334C8.1854 5.83334 8.33464 5.6841 8.33464 5.50001C8.33464 5.31591 8.1854 5.16667 8.0013 5.16667C7.81721 5.16667 7.66797 5.31591 7.66797 5.50001C7.66797 5.6841 7.81721 5.83334 8.0013 5.83334Z" fill="black" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.0013 7.83334C11.1854 7.83334 11.3346 7.6841 11.3346 7.50001C11.3346 7.31591 11.1854 7.16667 11.0013 7.16667C10.8172 7.16667 10.668 7.31591 10.668 7.50001C10.668 7.6841 10.8172 7.83334 11.0013 7.83334Z" fill="black" stroke="currentColor" strokeWidth="1.33333" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    color: 'purple',
  },
};

type MiloMarkerProps = {
  experience: Experience;
};

const MiloMarker: ReactFC<MiloMarkerProps> = ({ experience, children, className }) => {
  const { user } = useAuthentication();
  const [, saveLastClickedExperienceId] = useLocalStorage(MAP_LAST_CLICKED_EXPERIENCE_LS_KEY);
  const markerRef = useRef<L.Marker>(null);
  const isInWishlist = user?.wishlists?.some((wishlistItem) =>
    wishlistItem.experiences.some((exp) => experience.id === exp.id)
  );
  const visited = user?.visited_this_year?.some((visitedId) => visitedId === experience.id);

  const handleMarkerClick = () => {
    saveLastClickedExperienceId(experience.id);
  };

  const markerBorderColor = isInWishlist
    ? colorFn('black')
    : palette[verticalsDesign?.[experience.vertical_id]?.['color']];
  const markerInsideColor = palette[verticalsDesign?.[experience.vertical_id]?.['color']];
  const htmlVerticalIcon = verticalsDesign?.[experience.vertical_id]?.['htmlIcon']('w-full');

  const MiloLogoMarker = L.divIcon({
    html: `
    <div class="h-8 w-8 flex items-center relative ${visited ? 'opacity-50' : ''}">
      <svg class="w-full" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.075 23.52C1.264 13.642 0 12.629 0 9C0 4.029 4.029 0 9 0C13.971 0 18 4.029 18 9C18 12.629 16.736 13.64 9.925 23.516C9.719 23.81 9.382 24 9 24C8.618 24 8.281 23.81 8.078 23.52L8.076 23.516L8.075 23.52Z" fill="${markerBorderColor}"/>
      <circle cx="9" cy="9" r="7" fill="${markerInsideColor}"/>
      <path d="M8.57826 21.3385C8.77484 21.6469 9.22505 21.6468 9.42154 21.3384L14.7344 13H3.26172L8.57826 21.3385Z" fill="${markerInsideColor}"/>
    </svg>
    <div 
      class="w-3/4 left-1/2 -translate-x-1/2 top-0 text-white absolute"
      style="z-index: ${zIndexFn('modal', -1)}"
    >
      ${htmlVerticalIcon}
    </div>
    </div>`,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -20],
    className,
  });

  return (
    <Marker
      ref={markerRef}
      icon={MiloLogoMarker}
      position={[experience?.location?.lat, experience?.location?.lng]}
      eventHandlers={{ click: handleMarkerClick }}
    >
      <Popup>{children}</Popup>
    </Marker>
  );
};

type MiloClusterProps = {
  lat: number;
  lng: number;
  pointCount: number;
  onClick: Function;
};

const MiloCluster: ReactFC<MiloClusterProps> = ({ onClick, pointCount = 0, lat, lng, ...props }) => {
  return (
    <Marker
      eventHandlers={{ click: () => onClick() }}
      position={[lat, lng]}
      icon={L.divIcon({
        html: `<div
                style="z-index: ${zIndexFn('modal', -1)}"
                class="text-[13px] bg-charcoal !w-9 !h-9 rounded-full text-white !flex !items-center !justify-center font-bold !p-1">
                  ${pointCount}
              </div>`,
        className: 'milo-cluster-marker animate-fade-in',
      })}
      {...props}
    ></Marker>
  );
};

const splitCloseProximityMarkers = (closeExperiencesArray: Experience[][]) => {
  const closeProximityStyleNodeId = 'closeProximityExperiencesStyles';

  const existingCloseProximityStyleNode = document.getElementById(closeProximityStyleNodeId);

  if (existingCloseProximityStyleNode) existingCloseProximityStyleNode.remove();

  const newCloseProximityStyleNode = document.createElement('style');
  newCloseProximityStyleNode.setAttribute('id', closeProximityStyleNodeId);

  let newCloseProximityStyleNodeContent = '';

  closeExperiencesArray.forEach((closeExperiences) => {
    closeExperiences.forEach((exp, index) => {
      const className = ['', ...getExperienceMarkerClassName(exp.id).split(' ')].join('.');

      newCloseProximityStyleNodeContent += `
        ${className} {
          left: ${33 * index}px !important;
        }
      `;
    });
  });

  newCloseProximityStyleNode.textContent = newCloseProximityStyleNodeContent;

  document.body.appendChild(newCloseProximityStyleNode);
};

const getExperienceMarkerClassName: (expId: number) => string = (expId) => {
  return `experience-marker ${EXPERIENCE_MARKER_CLASS_PREFIX}-${expId}`;
};

const getPointOfInterestIcon: (poi: PointOfInterest) => L.DivIcon = (poi) => {
  if (poi.type === 'festival') {
    return L.divIcon({
      html: `<svg class="w-8 h-8 animate-fade-in" version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" xml:space="preserve" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#000000" stroke-width="9.216"> <style type="text/css">  .st0{fill:#000000;}  </style> <g> <path class="st0" d="M457.031,271.672C351.25,239.828,253.016,124.375,253.016,124.375 c8.594,22.016,95.359,118.813,121.141,147.297H457.031z"></path> <path class="st0" d="M49,271.672h84.75c25.391-28.484,110.813-125.281,119.266-147.297C253.016,124.375,154.781,239.828,49,271.672 z"></path> <path class="st0" d="M253.016,124.375c0,0-30.297,104.641-52.969,147.297h52.969h53.797 C283.781,229.016,253.016,124.375,253.016,124.375z"></path> <path class="st0" d="M182.172,509.063c17.266,1.469,33.5,2.438,47.953,2.938v-61.188c0-14.297,11.594-25.875,25.875-25.875 s25.875,11.578,25.875,25.875V512c14.453-0.5,30.703-1.469,47.953-2.938c-4.5-63.297-11.688-172.938-14.203-211.516H256h-59.625 C193.859,336.125,186.688,445.766,182.172,509.063z"></path> <path class="st0" d="M420.797,496.594c26.031-5.078,51.469-11.594,74.047-19.906C468.969,375.172,463,297.547,463,297.547h-62.125 C408.938,407.234,415.906,465.719,420.797,496.594z"></path> <path class="st0" d="M111.125,297.547H49c0,0-5.969,77.625-31.844,179.141c22.578,8.313,48.016,14.828,74.047,19.906 C96.094,465.719,103.078,407.234,111.125,297.547z"></path> <polygon class="st0" points="274.5,2.141 328.609,25.734 328.609,43.438 362.438,63.797 313.063,70.594 313.063,62.188 274.5,60.047 "></polygon> <path class="st0" d="M242.344,9.656c0-5.344,4.313-9.656,9.641-9.656l0,0c5.328,0,9.641,4.313,9.641,9.656v100.781 c0,5.344-4.313,9.656-9.641,9.656l0,0c-5.328,0-9.641-4.313-9.641-9.656V9.656z"></path> </g> </g><g id="SVGRepo_iconCarrier"> <style type="text/css">  .st0{fill:#000000;}  </style> <g> <path class="st0" d="M457.031,271.672C351.25,239.828,253.016,124.375,253.016,124.375 c8.594,22.016,95.359,118.813,121.141,147.297H457.031z"></path> <path class="st0" d="M49,271.672h84.75c25.391-28.484,110.813-125.281,119.266-147.297C253.016,124.375,154.781,239.828,49,271.672 z"></path> <path class="st0" d="M253.016,124.375c0,0-30.297,104.641-52.969,147.297h52.969h53.797 C283.781,229.016,253.016,124.375,253.016,124.375z"></path> <path class="st0" d="M182.172,509.063c17.266,1.469,33.5,2.438,47.953,2.938v-61.188c0-14.297,11.594-25.875,25.875-25.875 s25.875,11.578,25.875,25.875V512c14.453-0.5,30.703-1.469,47.953-2.938c-4.5-63.297-11.688-172.938-14.203-211.516H256h-59.625 C193.859,336.125,186.688,445.766,182.172,509.063z"></path> <path class="st0" d="M420.797,496.594c26.031-5.078,51.469-11.594,74.047-19.906C468.969,375.172,463,297.547,463,297.547h-62.125 C408.938,407.234,415.906,465.719,420.797,496.594z"></path> <path class="st0" d="M111.125,297.547H49c0,0-5.969,77.625-31.844,179.141c22.578,8.313,48.016,14.828,74.047,19.906 C96.094,465.719,103.078,407.234,111.125,297.547z"></path> <polygon class="st0" points="274.5,2.141 328.609,25.734 328.609,43.438 362.438,63.797 313.063,70.594 313.063,62.188 274.5,60.047 "></polygon> <path class="st0" d="M242.344,9.656c0-5.344,4.313-9.656,9.641-9.656l0,0c5.328,0,9.641,4.313,9.641,9.656v100.781 c0,5.344-4.313,9.656-9.641,9.656l0,0c-5.328,0-9.641-4.313-9.641-9.656V9.656z"></path> </g> </g></svg>`,
      className: 'milo-poi-marker',
    });
  }
};

const MapMarkers: ReactFC<MiloMapProps> = ({ pointsOfInterest = [], experiences = [] }) => {
  const [savedMapZoom, saveZoom] = useLocalStorage(MAP_SAVED_ZOOM_LS_KEY);
  const [, saveCenter] = useLocalStorage(MAP_SAVED_CENTER_LS_KEY);
  const [savedBounds, saveBounds] = useLocalStorage(MAP_SAVED_BOUNDS_LS_KEY);
  const map = useMap();
  const { data: verticals = [] } = useVerticals();
  const { user } = useAuthentication();

  const experiencesAsPoints = experiences.map((exp) => ({
    type: 'experience',
    properties: { data: exp },
    geometry: {
      type: 'Point',
      coordinates: [exp.location.lat, exp.location.lng],
    },
  }));

  const pointsOfInterestAsPoints = pointsOfInterest.map((poi) => ({
    type: 'point_of_interest',
    properties: { data: poi },
    geometry: {
      type: 'Point',
      coordinates: [poi.lat, poi.lng],
    },
  }));

  const points = [...pointsOfInterestAsPoints, ...experiencesAsPoints];

  if (savedMapZoom === MAP_MAX_ZOOM) {
    const groupedByProximity = Object.values(
      groupBy(experiences, (exp) => `${exp.location.lat}-${exp.location.lng}`)
    ).filter((e) => e.length > 1);

    if (groupedByProximity.length > 0) {
      splitCloseProximityMarkers(groupedByProximity);
    }
  }

  const clusterRadius = savedMapZoom >= MAP_MAX_ZOOM ? -1 : 1000 / savedMapZoom;
  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: savedBounds,
    zoom: savedMapZoom,
    options: { radius: clusterRadius, maxZoom: MAP_MAX_ZOOM - 1 },
  });

  const updateMapProperties = debounce(() => {
    setNewMapBounds();
    setNewZoom();
    map.closePopup();
  }, 100);

  const setNewMapBounds = () => {
    const b = map.getBounds();
    const center = map.getCenter();
    saveCenter([center.lat, center.lng]);
    saveBounds([b.getSouth(), b.getWest(), b.getNorth(), b.getEast()]);
  };

  const setNewZoom = () => {
    const z = map.getZoom();
    saveZoom(z);
  };

  useMapEvents({
    zoom: updateMapProperties,
    drag: updateMapProperties,
  });

  useEffect(() => {
    updateMapProperties();
  }, [experiences.length]);

  return (
    <>
      {clusters.map((cluster) => {
        const data = cluster.properties.data;
        const [latitude, longitude] = cluster.geometry.coordinates;

        const markerType: MapMarkerType = (() => {
          if (cluster?.properties?.cluster) return 'cluster';
          if (cluster.type === 'point_of_interest') return 'point_of_interest';
          return 'experience';
        })();

        if (markerType === 'cluster') {
          return (
            <MiloCluster
              onClick={() =>
                map.flyTo(
                  [latitude, longitude],
                  Math.min(supercluster.getClusterExpansionZoom(cluster.id) + 1, MAP_MAX_ZOOM),
                  { duration: 0.75 }
                )
              }
              key={`cluster-${cluster.id}`}
              lat={latitude}
              lng={longitude}
              pointCount={cluster.properties.point_count}
            />
          );
        }

        if (markerType === 'point_of_interest') {
          const poi = data as PointOfInterest;

          return (
            <Marker
              key={`poi-${latitude}-${longitude}`}
              position={[latitude, longitude]}
              icon={getPointOfInterestIcon(cluster.properties.data as PointOfInterest)}
            >
              <Popup>
                <div className="relative flex !w-[280px] flex-col gap-1 overflow-hidden px-6 py-6">
                  <h2 className="mb-2 text-2xl font-semibold">{poi.name}</h2>
                  <div className="mb-1">
                    <p className="!m-0 mb-1.5 text-[15px] font-semibold">Ville</p>
                    <p className="!m-0">{poi.city}</p>
                  </div>

                  <div className="mb-3">
                    <p className="!m-0 mb-1.5 text-[15px] font-semibold">
                      <Trans>Attrait principal</Trans>
                    </p>
                    <p className="!m-0">{poi.primary_tag.name}</p>
                  </div>

                  {poi?.secondary_tags?.length > 0 && (
                    <div className="mb-3">
                      <p className="!m-0 mb-1.5 text-[15px] font-semibold">
                        <Trans>Attraits secondaires</Trans>
                      </p>
                      <p className="!m-0">
                        {poi.secondary_tags.map((s) => (
                          <p key={s.name} className="!m-0">
                            {s.name}
                          </p>
                        ))}
                      </p>
                    </div>
                  )}

                  {poi?.partner?.id && (
                    <div className="mb-3">
                      <style>{`
                    .poi-partner-logo {
                      height: 30px !important;
                      width: auto !important;
                    }
                  `}</style>
                      <div className="flex items-center gap-2">
                        <MiloRoundLogo width={30} />
                        <p className="!m-0 text-[25px] font-bold leading-[25px]">+</p>
                        <Link2 href={poi.partner?.website_url} popup>
                          <ImageKitImage title={poi.name} className="poi-partner-logo" src={poi.partner.logo_url} />
                        </Link2>
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="!m-0 text-[16px] font-bold">
                      {poi.link && (
                        <Link2 popup href={poi.link} underline>
                          <Trans>Infos sur l'événement</Trans>
                        </Link2>
                      )}
                    </p>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        }

        const exp = data as Experience;
        const isInWishlist = user?.wishlists?.some((wishlistItem) =>
          wishlistItem.experiences.some((experience) => experience.id === exp.id)
        );
        const visitedThisYear = user?.visits_this_year?.find((visit: Visit) => visit.experience.id === exp.id);

        // temp fix for experience 95:
        if (exp.id == 95) exp.vertical_id = 1;

        const VerticalIcon = verticalsDesign?.[exp.vertical_id]?.['reactIcon'];
        const verticalColor = colorFn(verticalsDesign?.[exp.vertical_id]?.['color']);

        return (
          <MiloMarker
            className={`animate-fade-in ${getExperienceMarkerClassName(exp.id)}`}
            experience={exp}
            key={getExperienceMarkerClassName(exp.id)}
          >
            <div className="relative !w-[280px] overflow-hidden px-6 pt-6">
              <div>
                <Text
                  className="!m-0 !mb-2 flex w-fit items-center font-bold"
                  style={{ color: verticalColor, borderColor: verticalColor }}
                >
                  {verticals?.find((c) => c.id === exp.vertical_id)?.name || ''}
                  <VerticalIcon className="ml-1 h-7 w-7" />
                </Text>

                <Text size="h3" className="!m-0 !mb-2 font-bold">
                  {exp.name}
                </Text>
                {visitedThisYear && (
                  <Text className="!m-0 !mb-2 font-semibold" muted>
                    <Trans>Déjà visité le: {convertDateToReadableFormat(visitedThisYear.date_created)}</Trans>
                  </Text>
                )}

                <div className="flex flex-col">
                  <Link2 href={`/experiences/${exp.slug}`} className="w-full">
                    <div className="relative !h-44 !w-full overflow-hidden rounded-lg">
                      <div className="absolute bottom-0 left-0 right-0 top-0 w-full bg-gradient-to-b from-charcoal/60" />
                      <Text className="absolute left-0 top-0 !m-0 !mb-2 p-2 font-bold text-white" size="h3">
                        -{formatCurrency(exp.savings_max)}
                      </Text>
                      <ImageKitImage
                        transforms="q-50"
                        className="!h-full !w-full rounded-lg object-cover"
                        imageFileName={exp.main_image}
                      />
                    </div>
                  </Link2>

                  <div className="!m-0 flex h-14 w-full items-center">
                    <button className="flex w-1/3 justify-center rounded-lg p-2 transition-all hover:bg-gray-lighter">
                      <AddWishlistModal experience={exp}>
                        {isInWishlist && <Heart className="h-6 w-6" fill="dark" stroke="dark" />}
                        {!isInWishlist && <Heart className="h-6 w-6" fill="transparent" />}
                      </AddWishlistModal>
                    </button>

                    <button
                      className="flex w-1/3 justify-center rounded-lg p-2 transition-all hover:bg-gray-lighter"
                      onClick={() => map.flyTo([exp.location.lat, exp.location.lng], 18, { duration: 0.75 })}
                    >
                      <ZoomIn className="h-6 w-6" />
                    </button>

                    <Link2
                      href={`/experiences/${exp.slug}`}
                      className="flex w-1/3 justify-center rounded-lg p-2 transition-all hover:bg-gray-lighter"
                      style={{ color: 'currentcolor' }}
                    >
                      <DocumentIcon className="h-6 w-6" />
                    </Link2>
                  </div>
                </div>
              </div>
            </div>
          </MiloMarker>
        );
      })}
    </>
  );
};

const MiloMap: ReactFC<MiloMapProps> = ({
  pointsOfInterest: passedPointsOfInterest,
  experiences: passedExperiences = [],
  zoomToFitPassedExperiences = false,
  includeCurrentPositionInBounds = false,
}) => {
  const [openFilter, setOpenFilter] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [experiencesFiltered, setExperiencesFiltered] = useState<Experience[]>([]);

  const { user, isLoading: userLoading } = useAuthentication();

  const pointsOfInterestOverriden = typeof passedPointsOfInterest !== 'undefined' && passedPointsOfInterest.length > 0;

  const { isLoading: pointsOfInterestLoading, data: pointsOfInterestActive } = useFeatureFlag(
    'points-of-interest',
    { employee: (user?.email ?? '').includes('@miloguide.com') || (user?.email ?? '').includes('@mtaregion.com') },
    { enabled: !userLoading && !pointsOfInterestOverriden }
  );

  const { data: pointsOfInterest } = usePointsOfInterest({
    options: { enabled: pointsOfInterestActive && !pointsOfInterestOverriden },
  });

  const finalPointsOfInterest = pointsOfInterestOverriden ? passedPointsOfInterest : pointsOfInterest;

  const { isLoading: experiencesLoading } = useQuery(['MiloMapExperiences'], async () => {
    if (passedExperiences.length > 0) {
      setExperiencesFiltered(passedExperiences);
      setExperiences(passedExperiences);
      return passedExperiences;
    }

    const experiences = await getExperiences();
    setExperiencesFiltered(experiences);
    setExperiences(experiences);
    return experiences;
  });

  const loading = experiencesLoading || pointsOfInterestLoading;

  if (loading) return <MiloMapLoader />;

  return (
    <>
      <MiloMapContainer
        pointsOfInterest={finalPointsOfInterest}
        experiences={experiencesFiltered}
        zoomToFitPassedExperiences={zoomToFitPassedExperiences}
        onClickFilter={() => setOpenFilter(true)}
        activeFiltersCount={activeFiltersCount}
        includeCurrentPositionInBounds={includeCurrentPositionInBounds}
      >
        <MapMarkers pointsOfInterest={finalPointsOfInterest} experiences={experiencesFiltered} />
      </MiloMapContainer>
      <MapFilterModal
        open={openFilter}
        onRequestClose={() => setOpenFilter(false)}
        experiences={experiences}
        appliedFilters={appliedFilters}
        onApply={(filtered, appliedFilters, activeFiltersCount) => {
          setAppliedFilters(appliedFilters);
          setExperiencesFiltered(filtered);
          setActiveFiltersCount(activeFiltersCount);
        }}
      />
    </>
  );
};

export default MiloMap;
